import Vue from 'vue';
import CalendarMixins from '../../../../shared/mixins/calendar-mixins';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import BundleCalendarCell from '../../../calendar/partials/TeamCalendarCell';
import { money } from '@jgile/laravel-vue/resources/js/util';
import min from 'lodash/min';

const BundleCalendarCellClass = Vue.extend(BundleCalendarCell);
export default {
    mixins: [CalendarMixins],
    props: {
        asset: {
            type: Object,
            required: true,
        },
        leadTime: {
            required: true,
        },
        busy: {
            default: false,
        },
    },

    data() {
        return {
            form: new SparkForm({
                end_date: null,
                start_date: null,
                enabled: 1,
                base_price: null,
                units_available: null,
                units_posted: null,
                bundle_assets: null,
                note: null,
            }),
            groupForm: new SparkForm({
                home_games: false,
                road_games: false,
                enabled: 1,
                base_price: null,
                units_available: null,
                bundle_assets: null,
                units_posted: null,
            }),
            pickerConfig: {
                minDate: moment().format('Y-M-D'),
            },
            leadTimeDate: null,
            columns: {
                asset_name: 'Asset Name',
                category: 'Category',
                online_unit_price: 'Online Unit Price',
                units_available: 'Units Available',
                units_per_bundle: 'Units Per Bundle',
            },
            showModal: false,
            suggestedPricing: {},
        };
    },
    computed: {
        ...mapGetters('bundle', [
            'getEvents',
            'products',
            'productByDate',
            'getProductAttribute',
            'suggestedPrice',
            'bundleAssets',
            'bundleAssetsCount',
            'bundleDates',
        ]),
        calendarConfig() {
            return {
                canSelectPast: false,
                defaultDate: _.get(this.asset, 'start_date', dateTimeObject()),
                params: {
                    bundle_dates: this.bundleDates,
                },
            };
        },
        eventName() {
            return _.get(this.calendar, 'selectedScheduleDate.title');
        },
        modalDay() {
            return moment(this.calendar.selectedEvent.start).format('dddd');
        },
        modalDate() {
            return moment(this.calendar.selectedEvent.start).date();
        },
        /**
         * returns the dynamic pricng adjusted price for form display
         */
        adjustedPrice() {
            let unitPrice = _.toNumber(this.form.base_price);
            return Math.ceil(
                this.calendar.selectedPriceModifier && unitPrice ? unitPrice + this.calendar.selectedPriceModifier.multiplier * unitPrice : unitPrice,
            );
        },
        /**
         * Url for data loading into calendar - required for mixin.
         */
        dataUrl() {
            return '/api/team/assets/' + this.asset.id + '/calendar';
        },
        currentSchedule() {
            return _.has(Spark, 'currentSchedule.id');
        },
        calendarSelectionEditable() {
            return !this.beforeLeadTime(_.get(this.calendar, 'selectedStartDate.start'));
        },
    },

    created() {
        this.calendar.singleDaySelect = true;
        Vue.set(this.groupForm, 'home_games', this.asset.home_games);
        Vue.set(this.groupForm, 'road_games', this.asset.road_games);
        Bus.$on('calendarDatepickerChange', this.addCustomPricingGroup);
        this.$watch('asset.end_date', this.refetchEvents, { deep: true });
        this.$watch('asset.start_date', this.refetchEvents, { deep: true });
    },

    methods: {
        ...mapActions('bundle', ['setDefaultPromotionalProducts']),
        ...mapMutations('bundle', [
            'setProducts',
            'addProduct',
            'setSuggestedPrice',
            'setInitialAsset',
            'setProductAttribute',
            'setAssetAvailability',
            'setSavedProducts',
            'setPriceModifiers',
            'setSelectedDates',
            'setCalendarDates',
        ]),

        beforeLeadTime(date) {
            return !dateTimeObject(date).isAfter(this.leadTimeDate);
        },

        dayRender(date, cell) {
            if (this.beforeLeadTime(date)) {
                cell.addClass('fc-disabled-day');
            }
        },
        eventRender(event, element) {
            const instance = new BundleCalendarCellClass({
                propsData: {
                    event: event,
                    element: element,
                    config: { icons: true },
                },
            });
            instance.$mount();
            element.html(instance.$el);
        },
        selectAllow(selectInfo) {
            if (this.asset.asset_type === 'promo_bundle') {
                if (selectInfo.end.diff(selectInfo.start, 'hours') > 24) {
                    return false;
                }
                if (!this.productByDate(selectInfo.start.startOf('day').format('YYYY-MM-DD'))) {
                    return false;
                }
            }

            return true;
        },
        onGetCalendarData(events) {
            this.setInitialAsset(this.asset);
            this.setCalendarDates({
                start_date: events.start_date,
                end_date: events.end_date,
            });
            this.suggestedPricing = events.suggested_pricing;
            this.setSavedProducts(events.products);
            this.setPriceModifiers(events.price_modifiers);
            this.setAssetAvailability(events.asset_availability);

            if (this.asset.asset_type === 'promo_bundle') {
                this.setProducts({});
                this.setDefaultPromotionalProducts();
                this.setSuggestedPrice(_.get(this.calendar, 'meta.suggested_price', 0));
            }

            this.calendar.events = this.getEvents(this.formatGamedayEvent);
        },

        formatGamedayEvent(state, start, end, new_product, current_product, assets, asset_availability, price_modifier) {
            let startString = dateTimeString(start);
            let product = _.merge({}, _.defaultTo(current_product, {}), _.defaultTo(new_product, {}));
            let price = _.get(this.suggestedPricing, startString, 0);
            let errors = [];
            let table = [];
            let allUnits = [];

            _.each(assets, (asset) => {
                let unitsAvailable = _.get(asset_availability, asset.id, 0);
                allUnits.push(unitsAvailable);

                table.push({
                    asset_name: asset.name,
                    category: asset.category.name,
                    online_unit_price: money(price),
                    units_available: unitsAvailable,
                    units_per_bundle: _.get(asset, 'pivot.units', 0),
                });

                if (_.get(asset, 'pivot.units', 0) > unitsAvailable) {
                    errors.push(`${asset.name} has ${unitsAvailable} units available.`);
                }
            });

            let expired = this.beforeLeadTime(start);

            return {
                id: 'asset' + start,
                asset_type: this.asset.asset_type,
                title: money(price),
                maxUnits: min(allUnits),
                product: product,
                start: startString,
                end: startString,
                errors: errors,
                table: table,
                expired: expired,
                className: [price ? 'product' : '', _.get(product, 'enabled') ? 'available' : 'unavailable', expired ? 'expired' : ''],
            };
        },

        onCalendarSelection(start, end) {
            this.setSelectedDates({ start_date: start, end_date: end });
            this.setFormValues(this.calendar.selectedEvent);

            if (this.asset.asset_type === 'promo_bundle') {
                Bus.$emit('openBundleCalendarDateModal', {
                    bundle_units: _.reduce(
                        this.bundleAssets,
                        (result, bundleAsset) => {
                            result[bundleAsset.id] = bundleAsset.pivot.units;
                            return result;
                        },
                        {},
                    ),
                    asset_id: this.asset.id,
                    start_date: start,
                    end_date: end,
                });
            }
        },
        setFormValues(event) {
            this.form.enabled = _.get(event, 'product.enabled', 1);
            this.form.base_price = _.get(event, 'product.base_price', 0) ? _.get(event, 'product.base_price', 0) : _.get(this, 'suggestedPrice', 0);
            this.form.units_available = _.get(event, 'product.units_available_remaining', this.asset.base_units_available);
            this.form.units_posted = _.get(event, 'product.units_posted', this.asset.base_units_posted);
            this.form.note = _.get(event, 'product.note');
            this.form.start_date = this.calendar.start_string;
            this.form.end_date = dateTimeString(this.calendar.end_date.subtract(1, 'days'));
        },
        cancel() {
            this.setFormValues([]);
            this.calendar.obj.fullCalendar('unselect');
            this.clearCalendarSelection();
        },
        save() {
            Spark.post(`/api/team/assets/${this.asset.id}/products`, this.form).then(() => {
                this.calendar.haveSelection = false;
                this.refetchEvents();
            });
        },
        clearPricing() {
            const _this = this;
            swal({
                title: 'Are you sure?',
                type: 'warning',
                confirmButtonClass: 'btn btn-danger mr-2',
                cancelButtonClass: 'btn btn-cancel',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Yes, I\'m sure',
                cancelButtonText: 'Cancel',
                html: 'This will delete all the pricing for this asset!',
            }).then((response) => {
                if (response.dismiss !== 'cancel') {
                    _this.calendar.isLoading = true;
                    axios.delete('/api/team/assets/' + this.asset.id + '/products/clear').then(() => {
                        _this.calendar.obj.fullCalendar('unselect');
                        _this.calendar.obj.fullCalendar('refetchEvents');
                        _this.setFormValues([]);
                        _this.setProducts([]);
                        _this.clearCalendarSelection();
                        Vue.set(this.groupForm, 'home_games', 0);
                        Vue.set(this.groupForm, 'road_games', 0);
                    });
                }
            });
        },
        addCustomPricingGroup() {
            const _this = this;

            swal({
                title: 'Apply pricing to selected date range?',
                type: 'warning',
                confirmButtonClass: 'btn btn-danger mr-2',
                cancelButtonClass: 'btn btn-cancel',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Apply',
                cancelButtonText: 'Cancel',
            }).then((response) => {
                if (response.dismiss !== 'cancel') {
                    _this.calendar.isLoading = true;
                    _this.form.start_date = _this.calendar.start_date;
                    _this.form.end_date = _this.calendar.end_date ? _this.calendar.end_date.clone().subtract(1, 'days') : _this.calendar.start_date;
                    _this.form.base_price = _this.asset.online_price;
                    _this.form.units_available = _this.asset.base_units_available;
                    _this.form.units_posted = _this.asset.base_units_posted;

                    Spark.post('/api/team/assets/' + _this.asset.id + '/products', _this.form).then(() => {
                        _this.$noty.success('Prices Saved');
                        _this.calendar.obj.fullCalendar('refetchEvents');
                    });
                }
            });
        },
        addPricingGroup(attr, message) {
            const _this = this;

            let titles = {
                home_games: 'Apply pricing to home games and override any previous input pricing',
                road_games: 'Apply pricing to road games and override any previous input pricing',
            };

            swal({
                title: _.get(titles, attr),
                type: 'warning',
                confirmButtonClass: 'btn btn-danger mr-2',
                cancelButtonClass: 'btn btn-cancel',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Apply',
                cancelButtonText: 'Cancel',
            }).then((response) => {
                if (response.dismiss !== 'cancel') {
                    _this.calendar.isLoading = true;
                    _this.groupForm.base_price = _this.suggestedPrice;
                    _this.groupForm.units_available = _this.asset.base_units_available;
                    _this.groupForm.units_posted = _this.asset.base_units_posted;
                    _this.groupForm.bundle_assets = _.map(this.bundleAssets, function(o) {
                        return { asset_id: o.id, units: o.pivot.units };
                    });

                    Spark.post('/api/team/assets/' + _this.asset.id + '/pricing-calendar/' + attr, this.groupForm)
                        .then((response) => {
                            let data = _this.$vueData.getData(response);
                            _this.groupForm.home_games = data.home_games;
                            _this.groupForm.road_games = data.road_games;
                            _this.$noty.success('Prices Saved');
                            this.calendar.obj.fullCalendar('refetchEvents');
                        })
                        .catch((response) => {
                            this.calendar.obj.fullCalendar('refetchEvents');

                            _this.$nextTick(function() {
                                if ($('.invalid-feedback.d-block').length) {
                                    $('html, body').animate(
                                        {
                                            scrollTop: $('.invalid-feedback.d-block:first').offset().top - 100,
                                        },
                                        500,
                                    );
                                }
                            });
                        });
                } else {
                    Vue.set(_this.groupForm, attr, 0);
                }
            });
        },
        removePricingGroup(attr, message) {
            const _this = this;

            let titles = {
                home_games: 'Remove pricing from home games?',
                road_games: 'Remove pricing from road games?',
            };

            swal({
                title: _.get(titles, attr),
                type: 'warning',
                confirmButtonClass: 'btn btn-danger mr-2',
                cancelButtonClass: 'btn btn-cancel',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'Cancel',
            }).then((response) => {
                if (response.dismiss !== 'cancel') {
                    _this.calendar.isLoading = true;
                    axios.delete('/api/team/assets/' + _this.asset.id + '/pricing-calendar/' + attr).then((response) => {
                        let data = _this.$vueData.getData(response);
                        _this.groupForm.home_games = data.home_games;
                        _this.groupForm.road_games = data.road_games;
                        _this.$noty.success('Prices removed');
                        this.calendar.obj.fullCalendar('refetchEvents');
                    });
                } else {
                    Vue.set(_this.groupForm, attr, 1);
                }
            });
        },
        pricingMethod(e) {
            let attr = e.target.dataset.attr;

            if (e.target.checked) {
                this.addPricingGroup(attr);
            } else {
                this.removePricingGroup(attr);
            }
        },
    },
    watch: {
        'calendar.haveSelection': {
            deep: true,
            handler(newVal) {
                this.showModal = newVal;
                if ('activeElement' in document) {
                    document.activeElement.blur();
                }
            },
        },
        asset: {
            deep: true,
            handler(newVal) {
                this.setInitialAsset(newVal);
            },
        },
        leadTime: {
            deep: true,
            handler(newVal) {
                this.leadTimeDate = dateTimeObject().businessAdd(_.get(newVal, 'days', 5));
                if (this.calendar.obj) {
                    this.calendar.obj.fullCalendar('destroy');
                    this.$nextTick(() => {
                        this.buildCalendar();
                    });
                }
            },
        },
    },
};
